import type { TFunction } from 'i18next'

import { ClerkUserRole } from '@/types/auth'
import { NOTIFICATION_SOURCE_TYPES, type AppNotification } from '@/types/notifications'

// How a stored notification becomes two lines and a destination (ENG-3382).
//
// Both are derived at display time rather than stored: a notification written
// three weeks ago has to read in whatever language its reader uses now, and a
// route rename has to be a change here rather than a data migration.

/** Which side of a coaching relationship the reader is on.
 *
 *  Derived from the reader's **role**, not from which shell drew the bell. Both
 *  are tempting and only one is correct: the learner header is shared, so a coach
 *  reading it there is still a coach, and their conversations live on the coach
 *  screen. Sending them to the learner route lands on a guard that does not admit
 *  their role at all. It decides the copy for the same reason: a meeting notice
 *  is addressed to both parties and each side's copy names the other. */
export type NotificationSurface = 'learner' | 'coach'

/** The two lines of one entry. `detail` is optional; several coach rows are a
 *  title and a timestamp. */
export type NotificationLines = {
  readonly title: string
  readonly detail: string
}

/**
 * Translation key for one topic.
 *
 * Dots are replaced because i18next treats `.` as a key separator, so a dotted
 * topic key looked up verbatim descends into `coaching` and finds nothing.
 *
 * The coach surface looks for a `_coach` variant first: the two sides of a
 * meeting notice name different people.
 */
export function translationKeyFor(topicKey: string, surface: NotificationSurface): string {
  const flat = topicKey.replaceAll('.', '_')
  // A topic that is already the coach's own (the "missed" and "replied" pairs are
  // separate topics, not variants) gets no second suffix: `..._coach_coach` would
  // never exist, and every render would log a missing key on its way to the
  // fallback.
  const wantsVariant = surface === 'coach' && !flat.endsWith('_coach')
  return `notifications.items.${flat}${wantsVariant ? '_coach' : ''}`
}

/** The title and detail for one notification, in the reader's current language. */
export function notificationLines(
  t: TFunction,
  notification: AppNotification,
  surface: NotificationSurface,
  locale: string
): NotificationLines {
  const { payload } = notification
  const values = {
    coachName: payload.coach_name || t('notifications.yourCoach'),
    learnerName: payload.learner_name || t('notifications.aLearner'),
    startsAt: formatStartsAt(payload.starts_at, locale, payload.timezone),
    when: t(`notifications.when.${relativeDay(payload.starts_at, payload.timezone)}`),
  }

  const key = translationKeyFor(notification.topic_key, surface)
  const fallbackKey = translationKeyFor(notification.topic_key, 'learner')
  return {
    // An unknown topic renders the fallback rather than a blank row: a reader who
    // can see that something happened but not what it was cannot deal with it,
    // and cannot make it go away either.
    title: t(`${key}.title`, {
      ...values,
      defaultValue: t(`${fallbackKey}.title`, { ...values, defaultValue: t('notifications.fallback') }),
    }),
    detail: t(`${key}.detail`, {
      ...values,
      defaultValue: t(`${fallbackKey}.detail`, { ...values, defaultValue: '' }),
    }),
  }
}

/** `today`, `tomorrow`, or `soon` for anything further out.
 *
 *  Reminders are worded relatively because the dispatcher ticks every 30 seconds
 *  and a bell entry that promises a precise minute promises something the send
 *  path does not deliver. The exact time, with its zone, is on the detail line. */
function relativeDay(startsAt: string | undefined, timezone: string | undefined): 'today' | 'tomorrow' | 'soon' {
  if (!startsAt) return 'soon'
  const parsed = new Date(startsAt)
  if (Number.isNaN(parsed.getTime())) return 'soon'

  const zone = usableZone(timezone)
  const dayOf = (date: Date) => Date.parse(new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(date))
  const days = Math.round((dayOf(parsed) - dayOf(new Date())) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return 'soon'
}

/** The notice's own zone, the one its email states; the reader's for rows written before it was kept. */
function usableZone(timezone: string | undefined): string | undefined {
  if (!timezone) return undefined
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone })
    return timezone
  } catch {
    return undefined
  }
}

/** A meeting time, always with its zone named. */
function formatStartsAt(startsAt: string | undefined, locale: string, timezone: string | undefined): string {
  if (!startsAt) return ''
  const parsed = new Date(startsAt)
  if (Number.isNaN(parsed.getTime())) return ''
  // Components rather than `dateStyle`/`timeStyle`, which throw beside `timeZoneName`.
  return new Intl.DateTimeFormat(locale, {
    timeZone: usableZone(timezone),
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(parsed)
}

/**
 * Where clicking a notification goes, or `null` when there is nowhere.
 *
 * Derived from the source pair rather than a stored URL, which would freeze
 * today's routes into a table nothing rewrites.
 *
 * A meeting has nowhere to go on either surface yet (ENG-3359, ENG-3360,
 * ENG-3361), and deliberately does not fall back to the private-class routes: a
 * coaching session is a `live_session` row, so those pages might render one, and
 * "might" is not a reason to point a link at a screen built for another kind of
 * class.
 */
export function notificationDestination(notification: AppNotification, surface: NotificationSurface): string | null {
  const { source_type: sourceType, source_id: sourceId } = notification
  if (!sourceId) return null

  if (sourceType === NOTIFICATION_SOURCE_TYPES.coachMessage) {
    if (surface === 'learner') return '/coaching/messages'
    // The coach's screen opens a conversation by learner, and `source_id` is the
    // message, so the learner comes from the payload. Without one the row still
    // links, to the list rather than to the thread.
    const learnerUserId = notification.payload.learner_user_id
    return learnerUserId ? `/admin/my-messages?learner=${learnerUserId}` : '/admin/my-messages'
  }

  return null
}

/**
 * Which side of the relationship a role reads from.
 *
 * Everybody who is not a learner reads the coach side, because everybody who is
 * not a learner reaches a conversation through the coach screen: coaches own it,
 * and the admin roles that can open it get there the same way. A signed-out or
 * unresolved role reads as a learner, which is the safe default — the learner
 * route admits the admin roles, and the coach route admits nobody else.
 */
export function surfaceForRole(role: string | undefined): NotificationSurface {
  return role === ClerkUserRole.LEARNER || role === undefined ? 'learner' : 'coach'
}
